import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Heading,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { Promotion, PromotionLocation } from 'api/types';
import { SingleDatepicker } from 'components/DatePicker';
import { useUserContext } from 'context/UserContextProvider';
import { endOfDay, format, startOfDay, subHours } from 'date-fns';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { CreatePromotionMutationVariables, useCreatePromotionMutation } from './useCreatePromotionMutation';
import { UpdatePromotionMutationVariables, useUpdatePromotionMutation } from './useUpdatePromotionMutation';

type Props = {
  promotion: Promotion | null;
};

export const PromotionForm = ({ promotion }: Props) => {
  const { t } = useTranslation();
  const toast = useToast(defaultToastOptions);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updatePromotionMutation = useUpdatePromotionMutation(promotion?.id);
  const createPromotionMutation = useCreatePromotionMutation();
  const userContext = useUserContext();
  const allLanguages = useMemo(
    () =>
      Array.from(
        new Set(
          Object.entries(userContext.config.languages)
            .map(([_, languages]) => languages)
            .flat(),
        ),
      ),
    [userContext.config.languages],
  );
  const getLanguagesByCountryCodes = useCallback(
    (countryCodes: string[]) =>
      Array.from(
        new Set(
          Object.entries(userContext.config.languages)
            .filter(([country]) => countryCodes.includes(country))
            .map(([_, languages]) => languages)
            .flat(),
        ),
      ),
    [userContext.config.languages],
  );
  const countryCodes = useMemo(
    // Exclude XI (Nothern Ireland of UK) from countries
    () => Array.from(Object.values(userContext.config.countries).filter((code) => code !== 'XI')),
    [userContext.config.countries],
  );
  const countries = useMemo(
    () =>
      countryCodes.map((country) => ({
        value: country,
        label: t(`location:countries.${country}`),
      })),
    [countryCodes, t],
  );

  const billingCycleOptions = useMemo(
    () =>
      [
        { value: 'null', label: t('common:any') },
        { value: 'month', label: t('common:monthly') },
        { value: 'year', label: t('common:yearly') },
      ] as const,
    [t],
  );
  type BillingCycleValues = (typeof billingCycleOptions)[number]['value'];
  const payPerUseOptions = useMemo(
    () =>
      [
        { value: 'null', label: t('common:any') },
        { value: 'true', label: t('common:yes') },
        { value: 'false', label: t('common:no') },
      ] as const,
    [t],
  );
  type PayPerUseValues = (typeof payPerUseOptions)[number]['value'];
  const distributorOptions = useMemo(
    () =>
      [
        { value: 'null', label: t('common:any') },
        { value: 'true', label: t('common:yes') },
        { value: 'false', label: t('common:no') },
      ] as const,
    [t],
  );
  type DistributorValues = (typeof distributorOptions)[number]['value'];
  const systemPartnerOptions = useMemo(
    () =>
      [
        { value: 'null', label: t('common:any') },
        { value: 'true', label: t('common:yes') },
        { value: 'false', label: t('common:no') },
      ] as const,
    [t],
  );
  type SystemPartnerValues = (typeof systemPartnerOptions)[number]['value'];
  const customerTypeOptions = useMemo(
    () =>
      [
        { value: 'existing', label: t('promotions:criteria.customers.option.existing') },
        { value: 'new', label: t('promotions:criteria.customers.option.new') },
      ] as const,
    [t],
  );
  type CustomerTypeValues = (typeof customerTypeOptions)[number]['value'];
  const locationOptions = useMemo(
    () =>
      [
        { value: 'dashboard', label: t('promotions:location.option.dashboard') },
        { value: 'manage_subscription', label: t('promotions:location.option.manage_subscription') },
        { value: 'initial_checkout', label: t('promotions:location.option.initial_checkout') },
        { value: 'upgrade_checkout', label: t('promotions:location.option.upgrade_checkout') },
      ] as const satisfies {
        value: PromotionLocation;
        label: string;
      }[],
    [t],
  );

  const schema = useMemo(
    () =>
      yup
        .object({
          name: yup.string().required(t('forms:validation.generic.field.required', { fieldName: t('promotions:name.label') })),
          start: yup.date().required(t('forms:validation.generic.field.required', { fieldName: t('forms:active.from') })),
          end: yup.date().required(t('forms:validation.generic.field.required', { fieldName: t('forms:active.until') })),
          billing_cycle: yup
            .mixed<BillingCycleValues>()
            .required(t('forms:validation.generic.field.required', { fieldName: t('promotions:criteria.billing_cycle.label') }))
            .oneOf(
              ['month', 'year', 'null'],
              t('forms:validation.generic.field.required', { fieldName: t('promotions:criteria.billing_cycle.label') }),
            ),
          pay_per_use: yup
            .mixed<PayPerUseValues>()
            .required(t('forms:validation.generic.field.required', { fieldName: t('promotions:criteria.ppu.label') }))
            .oneOf(
              ['true', 'false', 'null'],
              t('forms:validation.generic.field.required', { fieldName: t('promotions:criteria.billing_cycle.label') }),
            ),
          distributor: yup
            .mixed<DistributorValues>()
            .required(t('forms:validation.generic.field.required', { fieldName: t('promotions:criteria.distributor.label') }))
            .oneOf(
              ['true', 'false', 'null'],
              t('forms:validation.generic.field.required', { fieldName: t('promotions:criteria.billing_cycle.label') }),
            ),
          system_partner: yup
            .mixed<SystemPartnerValues>()
            .required(t('forms:validation.generic.field.required', { fieldName: t('promotions:criteria.system_partner.label') }))
            .oneOf(
              ['true', 'false', 'null'],
              t('forms:validation.generic.field.required', { fieldName: t('promotions:criteria.billing_cycle.label') }),
            ),
          customer_type: yup
            .mixed<CustomerTypeValues>()
            .required(t('forms:validation.generic.field.required', { fieldName: t('promotions:criteria.customers.label') }))
            .oneOf(['new', 'existing'], t('forms:validation.generic.field.required', { fieldName: t('promotions:criteria.billing_cycle.label') })),
          remove_setup_fee: yup.boolean().required(t('forms:validation.generic.field.required', { fieldName: t('promotions:setup_fee.label') })),
          location: yup
            .array(yup.mixed<PromotionLocation>().required().oneOf(['dashboard', 'manage_subscription', 'initial_checkout', 'upgrade_checkout']))
            .min(1, t('promotions:form_validation.location.min', { count: 1 }))
            .required(),
          countries: yup
            .array(yup.string().required())
            .min(1, t('promotions:form_validation.country.min', { count: 1 }))
            .required(),
          texts: yup
            .array(
              yup.object({
                language: yup.string().required(),
                content: yup.string(),
              }),
            )
            .test({
              test(texts, context) {
                if (texts == null) {
                  return true;
                }
                const errors: yup.ValidationError[] = [];
                for (let i = 0; i < texts.length; i++) {
                  const text = texts[i];
                  // Show requried error for selected languages if text is empty
                  if (
                    getLanguagesByCountryCodes(context.parent.countries).includes(text.language) &&
                    (text.content == null || text.content.trim().length === 0)
                  ) {
                    errors.push(
                      context.createError({
                        message: t('forms:validation.generic.field.required', { fieldName: t(`promotions:texts.label`) }),
                        path: `texts.${i}.content`,
                      }),
                    );
                  }
                }
                if (errors.length === 0) {
                  return true;
                }
                throw new yup.ValidationError(errors);
              },
            })
            .required(),
        })
        .required(),
    [getLanguagesByCountryCodes, t],
  );
  const defaultValues = useMemo(
    () =>
      ({
        name: promotion?.name ?? '',
        start: promotion?.start != null ? new Date(promotion.start) : undefined,
        end: promotion?.end != null ? subHours(new Date(promotion.end), 11) : undefined,
        billing_cycle: promotion?.criteria.cycle ?? 'null',
        pay_per_use: promotion?.criteria.ppu === true ? 'true' : promotion?.criteria.ppu === false ? 'false' : 'null',
        distributor: promotion?.criteria.distributor === true ? 'true' : promotion?.criteria.distributor === false ? 'false' : 'null',
        system_partner: promotion?.criteria.systempartner === true ? 'true' : promotion?.criteria.systempartner === false ? 'false' : 'null',
        customer_type: promotion?.criteria.customer_type,
        remove_setup_fee: promotion?.remove_setup_fee === true,
        location: promotion?.location.filter((loc) => locationOptions.some(({ value }) => value === loc)) ?? [],
        countries: promotion?.country ?? [],
        texts: allLanguages.map((language) => ({ language, content: promotion?.texts.find((text) => text.language === language)?.value ?? '' })),
      }) satisfies Partial<yup.InferType<typeof schema>>,
    [promotion, allLanguages, locationOptions],
  );
  const { handleSubmit, control, getValues, setValue, watch, trigger, reset } = useForm({
    defaultValues: {
      ...defaultValues,
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const selectedCountries = watch('countries');
  const selectedLanguages = useMemo(() => getLanguagesByCountryCodes(selectedCountries), [getLanguagesByCountryCodes, selectedCountries]);

  useEffect(() => {
    setValue(
      'texts',
      allLanguages.map((language) => ({ language, content: getValues('texts').find((text) => text.language === language)?.content ?? '' })),
    );
  }, [allLanguages, getValues, setValue]);

  function onSubmit(data: yup.InferType<typeof schema>) {
    const promotionData: CreatePromotionMutationVariables & UpdatePromotionMutationVariables = {
      name: data.name,
      start: format(startOfDay(new Date(data.start)), 'yyyy-MM-dd HH:mm:ss'),
      end: format(endOfDay(new Date(data.end)), 'yyyy-MM-dd HH:mm:ss'),
      criteria: {
        cycle: data.billing_cycle === 'null' ? undefined : data.billing_cycle,
        ppu: data.pay_per_use === 'true' ? true : data.pay_per_use === 'false' ? false : undefined,
        distributor: data.distributor === 'true' ? true : data.distributor === 'false' ? false : undefined,
        systempartner: data.system_partner === 'true' ? true : data.system_partner === 'false' ? false : undefined,
        customer_type: data.customer_type,
      },
      remove_setup_fee: data.remove_setup_fee,
      location: data.location,
      country: data.countries,
      texts: data.texts
        .filter((text) => selectedLanguages.includes(text.language))
        .map((text) => ({
          language: text.language,
          value: text.content,
          promotion_id: promotion?.id,
        })),
    };

    if (promotion == null) {
      createPromotionMutation.mutate(promotionData, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['Promotions'] });
          navigate(`/app/op/promotions`);
          toast({
            title: t('promotions:toast.create.success.title'),
            status: 'success',
          });
        },
        onError: (error) => {
          toast({
            title: (error as any)?.response?.data?.message ?? t('promotions:toast.create.error.title'),
            status: 'error',
          });
        },
      });
    } else {
      updatePromotionMutation.mutate(promotionData, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['Promotions'] });
          navigate(`/app/op/promotions`);
          toast({
            title: t('promotions:toast.update.success.title'),
            status: 'success',
          });
        },
        onError: (error) => {
          toast({
            title: (error as any)?.response?.data?.message ?? t('promotions:toast.update.error.title'),
            status: 'error',
          });
        },
      });
    }
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} maxWidth="6xl" noValidate>
      <Box as="section">
        <Heading as="h2" size="xss" mb="4">
          {t('promotions:name.heading')}
        </Heading>

        <Stack as="section" direction="column" gap="6">
          <Controller
            name="name"
            render={({ field, fieldState }) => (
              <FormControl isInvalid={fieldState.invalid} isRequired>
                <FormLabel>{t('promotions:name.label')}</FormLabel>
                <Input {...field} />
                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
              </FormControl>
            )}
            control={control}
          />
        </Stack>
      </Box>

      <Box as="section" mt="12">
        <Heading as="h2" size="xss" mb="4">
          {t('promotions:interval.heading')}
        </Heading>
        <Stack direction="row" gap="8" flexWrap="wrap">
          <Controller
            name="start"
            render={({ field: { value, onChange, ref, ...field }, fieldState }) => (
              <FormControl isInvalid={fieldState.invalid} maxWidth="32ch">
                <FormLabel>{t('forms:active.from')}</FormLabel>
                <SingleDatepicker date={value ?? undefined} onDateChange={onChange} configs={{ dateFormat: 'dd.MM.yyyy' }} {...field} />
                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
              </FormControl>
            )}
            control={control}
          />
          <Controller
            name="end"
            render={({ field: { value, onChange, ref, ...field }, fieldState }) => (
              <FormControl isInvalid={fieldState.invalid} maxWidth="32ch">
                <FormLabel>{t('forms:active.until')}</FormLabel>
                <SingleDatepicker date={value ?? undefined} onDateChange={onChange} configs={{ dateFormat: 'dd.MM.yyyy' }} {...field} />
                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
              </FormControl>
            )}
            control={control}
          />
        </Stack>
      </Box>

      <Box as="section" mt="12">
        <Heading as="h2" size="xss" mb="4">
          {t('promotions:criteria.heading')}
        </Heading>

        <Grid gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" columnGap="2" rowGap="8">
          <Controller
            name="billing_cycle"
            render={({ field, fieldState }) => (
              <FormControl isInvalid={fieldState.invalid} width="initial">
                <FormLabel>{t('promotions:criteria.billing_cycle.label')}</FormLabel>
                <RadioGroup {...field} value={field.value}>
                  <Stack>
                    {billingCycleOptions.map((option) => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
              </FormControl>
            )}
            control={control}
          />
          <Controller
            name="pay_per_use"
            render={({ field, fieldState }) => (
              <FormControl isInvalid={fieldState.invalid} width="initial">
                <FormLabel>{t('promotions:criteria.ppu.label')}</FormLabel>
                <RadioGroup {...field}>
                  <Stack>
                    {payPerUseOptions.map((option) => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
              </FormControl>
            )}
            control={control}
          />
          <Controller
            name="distributor"
            render={({ field, fieldState }) => (
              <FormControl isInvalid={fieldState.invalid} width="initial">
                <FormLabel>{t('promotions:criteria.distributor.label')}</FormLabel>
                <RadioGroup {...field}>
                  <Stack>
                    {distributorOptions.map((option) => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
              </FormControl>
            )}
            control={control}
          />
          <Controller
            name="system_partner"
            render={({ field, fieldState }) => (
              <FormControl isInvalid={fieldState.invalid} width="initial">
                <FormLabel>{t('promotions:criteria.system_partner.label')}</FormLabel>
                <RadioGroup {...field}>
                  <Stack>
                    {systemPartnerOptions.map((option) => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
              </FormControl>
            )}
            control={control}
          />
          <Controller
            name="customer_type"
            render={({ field, fieldState }) => (
              <FormControl isInvalid={fieldState.invalid} width="initial">
                <FormLabel>{t('promotions:criteria.customers.label')}</FormLabel>
                <RadioGroup {...field}>
                  <Stack>
                    {customerTypeOptions.map((option) => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
              </FormControl>
            )}
            control={control}
          />
        </Grid>
      </Box>

      <Box as="section" mt="12">
        <Heading as="h2" size="xss" mb="4">
          {t('promotions:setup_fee.heading')}
        </Heading>

        <Controller
          name="remove_setup_fee"
          render={({ field: { value, ...field }, fieldState }) => (
            <Box>
              <FormControl isInvalid={fieldState.invalid}>
                <Checkbox isChecked={value} {...field}>
                  {t('promotions:setup_fee.label')}
                </Checkbox>
                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
              </FormControl>
            </Box>
          )}
          control={control}
        />
      </Box>

      <Box as="section" mt="12">
        <Heading as="h2" size="xss" mb="4">
          {t('promotions:location.heading')}
        </Heading>

        <Controller
          name="location"
          render={({ field: { ref: _ref, ...field }, fieldState }) => (
            <FormControl isInvalid={fieldState.invalid}>
              <CheckboxGroup {...field}>
                <Stack direction="row" columnGap="8" flexWrap="wrap">
                  {locationOptions.map((option) => (
                    <Checkbox key={option.value} value={option.value}>
                      {option.label}
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
              <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
            </FormControl>
          )}
          control={control}
        />
      </Box>

      <Box as="section" mt="12">
        <Heading as="h2" size="xss" mb="4">
          {t('promotions:country.heading')}
        </Heading>

        <Controller
          name="countries"
          render={({ field: { ref: _ref, ...field }, fieldState }) => (
            <FormControl isInvalid={fieldState.invalid}>
              <CheckboxGroup {...field}>
                <Checkbox
                  isChecked={getValues('countries').length === countries.length}
                  isIndeterminate={getValues('countries').length > 0 && getValues('countries').length < countries.length}
                  onChange={(e) => {
                    setValue('countries', e.target.checked ? countries.map((c) => c.value) : []);
                    trigger('countries');
                  }}
                >
                  {t('forms:all_countries.label')}
                </Checkbox>
                <Grid gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap="2" mt="6">
                  {countries.map((option) => (
                    <Checkbox key={option.value} value={option.value} onChange={() => trigger('countries')}>
                      {option.label}
                    </Checkbox>
                  ))}
                </Grid>
              </CheckboxGroup>
              <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
            </FormControl>
          )}
          control={control}
        />
        <Box mt="8">
          {watch('texts').map((text, index) => (
            <Fragment key={text.language}>
              {selectedLanguages.includes(text.language) ? (
                <Controller
                  key={text.language}
                  name={`texts.${index}.content`}
                  render={({ field, fieldState }) => (
                    <FormControl isInvalid={fieldState.invalid} isRequired mt="4">
                      <FormLabel>{t(`location:languages.${text.language}`)}</FormLabel>
                      <Textarea rows={6} {...field} />
                      <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                  control={control}
                />
              ) : null}
            </Fragment>
          ))}
        </Box>
      </Box>

      <HStack justifyContent="end" mt="8">
        <Button variant="primary" type="submit">
          {promotion?.id == null ? t('common:create') : t('common:update')}
        </Button>
      </HStack>
    </Box>
  );
};
